import loadInitialRowsFromServer from "./loadInitialRowsFromServer";
import type { PerformanceDatapoint } from "owa-analytics";
import { returnTopExecutingActionDatapoint } from "owa-analytics";
import { initTableSelectionOnLoad } from "owa-mail-actions/lib/initTableSelectionOnLoad";
import type { OnInitialTableLoadComplete } from "owa-mail-loading-action-types";
import { createMailListLoadDatapoint } from "owa-mail-logging/lib/utils/mailListLoad/mailListLoadDatapointUtils";
import { markMailListLoadReadyToRender } from "owa-mail-logging/lib/utils/mailListLoad/mailListLoadDatapointGetterAndSetter";
import type { ActionSource } from "owa-mail-store";
import { updateTablesInCache } from "owa-mail-triage-local-updates/lib/actions/tableCache/updateTablesInCache";
import type { MailFolderTableQuery, SortBy, TableView } from "owa-mail-list-store";
import { type TableQueryType, type LoadErrorStatus, resetTableViewPropertiesMutator, isMailListConversationView, getGQLPagingInfoForQueryRowsForJumpTo } from "owa-mail-list-store";
import { mutatorAction } from "satcheljs";
import type { SessionData } from "owa-service/lib/types/SessionData";
import { DataSource } from "owa-analytics-types";
import { isOfflineMailDataEnabled } from "owa-offline-sync-feature-flags";
import { setCurrentLoadedIndexMutator } from "./setCurrentLoadedIndexMutator";
import { getGQLPagingInfoForQueryRowsById } from "owa-mail-list-store/lib/utils/getGqlTypesFromOwsTypes";
import { INITIAL_LOAD_ROW_COUNT } from "owa-mail-triage-common";
import isDeepLink from "owa-url/lib/isDeepLink";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";

/**
 * Performs the first load for a mail table
 * @param tableView - the given mail tableView
 * @param onInitialTableLoadComplete - callback for when the initial table load has completed
 * @param actionSource action that initiated the switch folder action
 * @param initialSessionData - session data to be sent with the initial load request
 * @param rowIdToLoad - optional rowId to begin loading from (used when maintaining or restoring selection)
 * @param seekData - optional seek data to jump to a specific location in the table
 * @return a promise that resolves when the load initial table has completed either from server or from cache
 */
export default function loadInitialMailTable(tableView: TableView, onInitialTableLoadComplete: OnInitialTableLoadComplete, actionSource: ActionSource, initialSessionData?: SessionData, rowIdToLoad?: string, seekData?: {
    matchingString: string;
    sortBy: SortBy;
}): Promise<void> {
    const isTableViewOfFolderType = tableView.tableQuery.type == 0;
    const switchMailFolderDatapoint = isTableViewOfFolderType
        ? returnTopExecutingActionDatapoint((dp: PerformanceDatapoint) => {
            return dp.getEventName() == "SwitchMailFolder";
        })
        : undefined;
    let mailListLoadId: number | undefined;
    // Create a new datapoint for the ML load for any user-initiated action source.
    // Deep-link apps do not render the mail list surface that completes this datapoint.
    if (shouldCreateMailListLoadDatapoint(tableView, actionSource)) {
        mailListLoadId = createMailListLoadDatapoint(actionSource, tableView, switchMailFolderDatapoint ?? undefined);
    }
    setCurrentLoadedIndexMutator(tableView);
    
    // Init table selection on any cached items we have
    initTableSelectionOnLoad(tableView, actionSource == "FolderNodeClick");
    
    // Update tables in cache
    updateTablesInCache(tableView);
    
    const scenarioType = (tableView.tableQuery as MailFolderTableQuery).scenarioType;
    
    if (switchMailFolderDatapoint) {
        switchMailFolderDatapoint.addToCustomWaterfall(2, "LoadInitialMailTable_S");
        switchMailFolderDatapoint.addCustomProperty("isCached", tableView.isInitialLoadComplete // table was loaded once before so its in the cache
        );
        switchMailFolderDatapoint.addCustomProperty("scenario", scenarioType);
        switchMailFolderDatapoint.addDataSource(tableView.isInitialLoadComplete ? DataSource.IN_MEMORY : DataSource.NETWORK);
        switchMailFolderDatapoint.addToCustomWaterfall(1, "isOfflineSyncEnabled", false /* discardIfDefined*/, isOfflineMailDataEnabled() ? "true" : "false");
    }
    
    const shouldShowCachedItems = tableView.isInitialLoadComplete && tableView.loadedStartIndex === 0;
    const shouldCompleteFromCachedRender = shouldShowCachedItems &&
        tableView.loadErrorStatus === 0 &&
        !rowIdToLoad &&
        !seekData;
    
    // Reset the table view properties, if we are not loaded from the top of the table
    // or if we are selecting a specific row in the list after a sort
    if (tableView.loadedStartIndex !== 0 || rowIdToLoad || seekData) {
        resetTableViewPropertiesMutator(tableView);
    }
    
    // After resetting, re-seed selectedRowKeys with the target row ID so that
    // onInitialTableLoadComplete sees hasSelection=true and skips autoSelectFirstRow.
    // Without this, autoSelectFirstRow skips pinned READ items (it skips all
    // pinned items in Date-sorted views), selecting the wrong row after a sort change.
    if (rowIdToLoad) {
        preserveTargetRowKeyMutator(tableView, rowIdToLoad);
    }
    
    // Create custom paging info if we have a selected row ID to maintain selection
    // or if a seekData object (string and sort column) was provided
    let customPagingInfo;
    const isConversationView = isMailListConversationView(tableView);
    if (rowIdToLoad) {
        customPagingInfo = getGQLPagingInfoForQueryRowsById(INITIAL_LOAD_ROW_COUNT, isConversationView, rowIdToLoad);
    }
    else if (seekData) {
        customPagingInfo = getGQLPagingInfoForQueryRowsForJumpTo(INITIAL_LOAD_ROW_COUNT, isConversationView, seekData.matchingString, (tableView.tableQuery as MailFolderTableQuery).sortBy);
    }
    
    const onLoadComplete: OnInitialTableLoadComplete = (completedTableView, isSuccessResponseClass, responseCode, isTablePrefetched) => {
        onInitialTableLoadComplete(completedTableView, isSuccessResponseClass, responseCode, isTablePrefetched);
        if (mailListLoadId !== undefined) {
            markMailListLoadReadyToRender(completedTableView.id, mailListLoadId);
        }
    };
    
    const loadFromServerPromise = loadInitialRowsFromServer(tableView, false /* isTablePrefetched */, onLoadComplete, initialSessionData, shouldShowCachedItems /* skipGqlTimings */, customPagingInfo);
    
    if (mailListLoadId !== undefined && shouldCompleteFromCachedRender) {
        markMailListLoadReadyToRender(tableView.id, mailListLoadId);
    }
    
    // Resolve the initial load promise immediately if we're showing cached items, otherwise resolve when the server returns results
    return shouldShowCachedItems ? Promise.resolve() : loadFromServerPromise;
}

function shouldCreateMailListLoadDatapoint(tableView: TableView, actionSource: ActionSource): boolean {
    const isNotesFolder = tableView.tableQuery.type === 0 &&
        folderIdToName(tableView.tableQuery.folderId, true /* shouldSearchShared */) === "notes";
    
    // Deep-link apps and Notes do not render the mail list surface that completes this datapoint.
    return !!actionSource && actionSource !== "NonUserAction" && !isDeepLink() && !isNotesFolder;
}

/**
 * Re-seeds selectedRowKeys with the target row key after resetTableViewProperties clears it.
 * This ensures onInitialTableLoadComplete sees hasSelection=true and does not call
 * autoSelectFirstRow, which would otherwise skip pinned READ items and select the wrong row.
 */
const preserveTargetRowKeyMutator = mutatorAction("preserveTargetRowKey", (tableView: TableView, rowKey: string) => {
    tableView.selectedRowKeys.set(rowKey, true);
});
